import { BlockRenderer, type CmsBlock } from './BlockRenderer';

interface RowProps {
  columnCount?: number;
  columnWidths?: string[];
  gap?: 'none' | 'sm' | 'md' | 'lg';
  verticalAlign?: 'top' | 'center' | 'bottom';
}

const WIDTH_MAP: Record<string, string> = {
  full: 'flex-1',
  '1/2': 'w-full sm:w-1/2',
  '1/3': 'w-full sm:w-1/3',
  '2/3': 'w-full sm:w-2/3',
  '1/4': 'w-full sm:w-1/4',
  '3/4': 'w-full sm:w-3/4',
};

const GAP_MAP: Record<string, string> = {
  none: 'gap-0',
  sm: 'gap-4',
  md: 'gap-8',
  lg: 'gap-16',
};

const ALIGN_MAP: Record<string, string> = {
  top: 'items-start',
  center: 'items-center',
  bottom: 'items-end',
};

export function RowBlock({ block }: { block: CmsBlock }) {
  const {
    columnCount = 1,
    columnWidths = ['full'],
    gap = 'md',
    verticalAlign = 'top',
  } = (block.props ?? {}) as RowProps;

  const children = block.children ?? [];

  // Group children by column index (_col prop)
  const columns: CmsBlock[][] = Array.from({ length: columnCount as number }, (_, i) =>
    children
      .filter((c) => ((c.props._col as number) ?? 0) === i)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  );

  const gapClass = GAP_MAP[gap] ?? 'gap-8';
  const alignClass = ALIGN_MAP[verticalAlign] ?? 'items-start';

  return (
    <div className={`flex flex-wrap ${gapClass} ${alignClass}`}>
      {columns.map((colBlocks, i) => {
        const widthKey = (columnWidths as string[])[i] ?? 'full';
        const widthClass = WIDTH_MAP[widthKey] ?? 'flex-1';
        return (
          <div key={i} className={`${widthClass} min-w-0 flex flex-col gap-6`}>
            {colBlocks.map((child) => (
              <BlockRenderer key={child.id} block={child} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
